/**
 * ORA - AI Leadership Agent
 * Copyright (c) 2024-2026 s2cpaul
 * Licensed under MIT License
 * Repository: https://github.com/s2cpaul/ora-agent
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { supabaseConfig } from './config';

// Create Supabase client with graceful fallback for development
let supabase: SupabaseClient | null = null;

try {
  // Only initialize if we have valid credentials
  if (supabaseConfig.url && supabaseConfig.anonKey && 
      supabaseConfig.url.startsWith('https://') && 
      supabaseConfig.anonKey.length > 20) {
    supabase = createClient(supabaseConfig.url, supabaseConfig.anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    });
    console.log('✅ Supabase client initialized successfully');
  } else {
    // In development, allow app to run without Supabase
    if (import.meta.env.DEV) {
      // Only show info once
      if (!window.__supabaseWarningShown) {
        window.__supabaseWarningShown = true;
        console.info(
          'ℹ️  ORA running in development mode - Backend features disabled (free tier works fine).'
        );
      }
      supabase = null;
    } else {
      throw new Error('Supabase credentials required in production');
    }
  }
} catch (error) {
  console.error('❌ Failed to initialize Supabase:', error);
  supabase = null;
}

// Export with type assertion (components should check if supabase exists before using)
export { supabase };

// Helper function to check if Supabase is available
export function isSupabaseAvailable(): boolean {
  return supabase !== null;
}

// Helper to safely use Supabase (throws helpful error if not configured)
export function requireSupabase(): SupabaseClient {
  if (!supabase) {
    throw new Error(
      'Supabase is not configured. Please set up your .env file.\n' +
      'See /QUICK_FIX_ERRORS.md for instructions.'
    );
  }
  return supabase;
}

// Database Types (we'll expand these as we create tables)
export interface Database {
  public: {
    Tables: {
      users: {
        Row: {
          id: string;
          email: string;
          full_name: string | null;
          subscription_tier: 'free' | 'solo' | 'buddy' | 'team';
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id: string;
          email: string;
          full_name?: string | null;
          subscription_tier?: 'free' | 'solo' | 'buddy' | 'team';
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          email?: string;
          full_name?: string | null;
          subscription_tier?: 'free' | 'solo' | 'buddy' | 'team';
          created_at?: string;
          updated_at?: string;
        };
      };
      subscriptions: {
        Row: {
          id: string;
          user_id: string;
          tier: 'free' | 'solo' | 'buddy' | 'team';
          status: 'active' | 'cancelled' | 'expired';
          stripe_subscription_id: string | null;
          current_period_start: string;
          current_period_end: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          tier: 'free' | 'solo' | 'buddy' | 'team';
          status?: 'active' | 'cancelled' | 'expired';
          stripe_subscription_id?: string | null;
          current_period_start: string;
          current_period_end: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          tier?: 'free' | 'solo' | 'buddy' | 'team';
          status?: 'active' | 'cancelled' | 'expired';
          stripe_subscription_id?: string | null;
          current_period_start?: string;
          current_period_end?: string;
          created_at?: string;
          updated_at?: string;
        };
      };
      usage_tracking: {
        Row: {
          id: string;
          user_id: string;
          month: string; // YYYY-MM format
          questions_asked: number;
          questions_limit: number;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          month: string;
          questions_asked?: number;
          questions_limit: number;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          month?: string;
          questions_asked?: number;
          questions_limit?: number;
          created_at?: string;
          updated_at?: string;
        };
      };
      quick_observations: {
        Row: {
          id: string;
          observation: string;
          additional_context: string | null;
          user_email: string | null;
          user_id: string | null;
          created_at: string;
        };
        Insert: {
          id?: string;
          observation: string;
          additional_context?: string | null;
          user_email?: string | null;
          user_id?: string | null;
          created_at?: string;
        };
        Update: {
          id?: string;
          observation?: string;
          additional_context?: string | null;
          user_email?: string | null;
          user_id?: string | null;
          created_at?: string;
        };
      };
    };
  };
}